import { useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { FormDialog, Field } from "@/admin/FormDialog";
import { ImageField } from "@/admin/ImageField";

const SERIES0 = { title: "", slug: "", description: "", banner_image: "", planned_books: 0, order: 0 };
const BOOK0 = { series_id: "", title: "", slug: "", number: 1, cover_image: "", kindle_url: "", blurb: "", release_date: "", order: 0 };

const useCrud = (path, refresh) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      form.id ? await api.put(`/${path}/${form.id}`, form) : await api.post(`/${path}`, form);
      toast.success("Saved"); setForm(null); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (item, label) => {
    if (!window.confirm(`Delete "${label}"?`)) return;
    await api.delete(`/${path}/${item.id}`); toast.success("Deleted"); refresh();
  };
  return { form, setForm, busy, save, remove, set: (p) => setForm((f) => ({ ...f, ...p })) };
};

const BookRow = ({ b, onEdit, onDelete }) => (
  <div data-testid={`admin-book-${b.slug}`} className="w-36">
    <div className="aspect-[2/3] border-2 border-[#c9a227] bg-[#1a120c] overflow-hidden">{b.cover_image && <img src={imgUrl(b.cover_image)} alt="" className="w-full h-full object-cover" />}</div>
    <p className="font-heading text-sm font-bold mt-2 leading-tight">{b.number}. {b.title}</p>
    <p className={`text-xs ${b.kindle_url ? "text-[#2f6b3a]" : "text-[#991b1b]"}`}>{b.kindle_url ? "Kindle linked" : "No Kindle link"}</p>
    <div className="flex gap-1 mt-1">
      <button onClick={onEdit} data-testid={`edit-book-${b.slug}`} className="p-1.5 hover:bg-[#c9a227]/20"><Pencil className="w-4 h-4" /></button>
      <button onClick={onDelete} data-testid={`delete-book-${b.slug}`} className="p-1.5 text-[#991b1b] hover:bg-[#991b1b]/10"><Trash2 className="w-4 h-4" /></button>
      {b.kindle_url && <a href={b.kindle_url} target="_blank" rel="noreferrer" className="p-1.5"><ExternalLink className="w-4 h-4" /></a>}
    </div>
  </div>
);

export default function AdminSeries() {
  const { series, refresh } = useSite();
  const S = useCrud("series", refresh);
  const B = useCrud("books", refresh);

  return (
    <div data-testid="admin-series">
      <AdminTitle eyebrow="The published chronicles" title="Series & Books">
        <button onClick={() => S.setForm({ ...SERIES0 })} data-testid="add-series-button" className="btn-illum theme-flame"><Plus className="w-4 h-4" /> Add series</button>
      </AdminTitle>
      <div className="space-y-8">
        {series.map((s) => (
          <section key={s.id} data-testid={`admin-series-${s.slug}`} className="vellum p-6">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl">{s.title}</h2>
                <p className="text-sm italic text-[#5e4a35]">{s.books.length} published · {s.planned_books} planned</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => B.setForm({ ...BOOK0, series_id: s.id, number: s.books.length + 1 })} data-testid={`add-book-${s.slug}`} className="btn-illum theme-frost !py-2"><Plus className="w-4 h-4" /> Add book</button>
                <button onClick={() => S.setForm({ ...s })} data-testid={`edit-series-${s.slug}`} className="btn-ghost-illum !py-2"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => S.remove(s, `${s.title}" and all its books`)} data-testid={`delete-series-${s.slug}`} className="btn-ghost-illum !py-2 !text-[#991b1b]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-6 mt-6">
              {s.books.map((b) => <BookRow key={b.id} b={b} onEdit={() => B.setForm({ ...b })} onDelete={() => B.remove(b, b.title)} />)}
              {s.books.length === 0 && <p className="italic text-sm">No books yet — they appear on the site as you add them.</p>}
            </div>
          </section>
        ))}
      </div>

      <FormDialog open={!!S.form} onOpenChange={() => S.setForm(null)} title={S.form?.id ? "Edit series" : "New series"} onSubmit={S.save} busy={S.busy} testid="series-dialog">
        {S.form && <>
          <Field label="Title"><input required data-testid="series-title-input" value={S.form.title} onChange={(e) => S.set({ title: e.target.value })} className="admin-input" /></Field>
          <Field label="Description"><textarea rows={3} data-testid="series-description-input" value={S.form.description} onChange={(e) => S.set({ description: e.target.value })} className="admin-input" /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Planned books"><input type="number" min="0" data-testid="series-planned-input" value={S.form.planned_books} onChange={(e) => S.set({ planned_books: Number(e.target.value) })} className="admin-input" /></Field>
            <Field label="Display order"><input type="number" value={S.form.order} onChange={(e) => S.set({ order: Number(e.target.value) })} className="admin-input" /></Field>
            <Field label="URL slug"><input value={S.form.slug} onChange={(e) => S.set({ slug: e.target.value })} placeholder="auto" className="admin-input" /></Field>
          </div>
          <ImageField label="Banner image" value={S.form.banner_image} onChange={(v) => S.set({ banner_image: v })} testid="series-banner-field" />
        </>}
      </FormDialog>

      <FormDialog open={!!B.form} onOpenChange={() => B.setForm(null)} title={B.form?.id ? "Edit book" : "New book"} onSubmit={B.save} busy={B.busy} testid="book-dialog">
        {B.form && <div className="grid sm:grid-cols-[11rem_1fr] gap-6">
          <ImageField label="Cover" value={B.form.cover_image} onChange={(v) => B.set({ cover_image: v })} aspect="aspect-[2/3]" testid="book-cover-field" />
          <div className="grid gap-4">
            <Field label="Title"><input required data-testid="book-title-input" value={B.form.title} onChange={(e) => B.set({ title: e.target.value })} className="admin-input" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Book number"><input type="number" min="1" data-testid="book-number-input" value={B.form.number} onChange={(e) => B.set({ number: Number(e.target.value) })} className="admin-input" /></Field>
              <Field label="Series">
                <select value={B.form.series_id} onChange={(e) => B.set({ series_id: e.target.value })} className="admin-input">{series.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}</select>
              </Field>
            </div>
            <Field label="Amazon Kindle URL"><input type="url" data-testid="book-kindle-input" value={B.form.kindle_url} onChange={(e) => B.set({ kindle_url: e.target.value })} placeholder="https://www.amazon.com/dp/…" className="admin-input" /></Field>
            <Field label="Short blurb (shown on cover hover)"><textarea rows={3} data-testid="book-blurb-input" value={B.form.blurb} onChange={(e) => B.set({ blurb: e.target.value })} className="admin-input" /></Field>
            <Field label="Release date"><input value={B.form.release_date} onChange={(e) => B.set({ release_date: e.target.value })} placeholder="e.g. March 2025" className="admin-input" /></Field>
          </div>
        </div>}
      </FormDialog>
    </div>
  );
}
